/**
 * Main converter: React Flow to Legacy Format
 * Orchestrates the conversion using modular converters
 */
import {
  findFixedHandleRouteConflicts,
  FIXED_SOURCE_HANDLES_BY_KIND,
} from '../../utils/fixedHandleRouteConflicts'
import {
  ALL_LEGACY_NODE_TYPES,
  DEFAULT_LABELS,
  NODE_TYPE_TO_KIND_MAP,
} from '../shared/constants/index'
import type {
  LegacyFormatData,
  LegacyFormatNode,
} from '../shared/legacyFormatTypes'
import type { LegacyNode, LegacyNodeKind } from '../shared/legacyNodes'
import type {
  SwitchboardEdge,
  SwitchboardFlowData,
  SwitchboardNode,
} from '../shared/reactFlowTypes'
import { NodeType } from '../shared/reactFlowTypes'
import {
  extractNumericId,
  generateSecureRandomNumber,
  getUniqueRandomNumber,
} from '../shared/uuidUtils'
import {
  validateEndpointReferences,
  validateTargetEndpointExists,
} from '../shared/validators'
import { buildEndpointMaps } from './endpointBuilders'
import { getHandleAnchorAndLabel } from './endpointBuilders'
import { convertNodeDataToHtml } from './nodeConverters/index'

export type { LegacyFormatData, LegacyFormatNode }

/**
 * Type for empty object used in legacy endpoint arrays
 */
type EmptyObject = Record<string, never>

/**
 * Node kinds that use dynamic handles (option-* handles)
 * These nodes require special handling for option_id extraction
 * Note: Legacy format only saves connected endpoints (no duplicates with target_uuid: null)
 */
const DYNAMIC_HANDLE_NODE_KINDS = new Set<LegacyNodeKind>([
  'call_distribution',
  'auto_attendant',
  'interactive_webhook',
])

/**
 * Node types and endpoint types that use [{}] pattern instead of []
 * Legacy uses [{}] for node types where legacy code expects at least one element
 * These are terminal nodes (no outgoing connections) that legacy code iterates over
 */
const EMPTY_ENDPOINT_PATTERN_MAP = new Map<
  LegacyNodeKind,
  Set<'sources' | 'targets'>
>([
  ['start_node', new Set(['targets'])],
  ['direct_line', new Set(['sources'])],
  ['leave_voicemail', new Set(['sources'])],
  ['hang_up', new Set(['sources'])],
  ['jump_to_dial_plan', new Set(['sources'])],
  ['conference_room', new Set(['sources'])],
])

/**
 * Get the empty endpoint pattern for a node type and endpoint type.
 *
 * Legacy uses two different patterns for empty endpoints:
 * - `[{}]` (array with empty object): Used for terminal nodes that legacy code expects
 *   to have at least one element in the array, even if empty
 * - `[]` (empty array): Used for all other node types
 *
 * Legacy Rationale:
 * Some legacy JavaScript code iterates over endpoint arrays and expects at least one element
 * to exist. For these node types, legacy uses `[{}]` to satisfy this expectation while still
 * indicating "no endpoints". The empty object serves as a placeholder that legacy code can
 * safely iterate over without errors.
 *
 * Node Types Using [{}] Pattern:
 * - `start_node.targets`: Start node targets array must have at least one element
 * - `direct_line.sources`: Terminal node - sources array must have at least one element
 * - `leave_voicemail.sources`: Terminal node - sources array must have at least one element
 * - `hang_up.sources`: Terminal node - sources array must have at least one element
 * - `jump_to_dial_plan.sources`: Terminal node - sources array must have at least one element
 * - `conference_room.sources`: Terminal node - sources array must have at least one element
 *
 * All other node types use `[]` (empty array) for empty endpoints.
 * The validator expects endpoints.sources to always be an array (accepts both [] and [{}]).
 *
 * @param kind - Legacy node kind
 * @param endpointType - 'sources' or 'targets'
 * @returns Empty endpoint pattern: [] or [{}]
 */
function getEmptyEndpointPattern(
  kind: LegacyNodeKind,
  endpointType: 'sources' | 'targets',
): [] | [EmptyObject] {
  const endpointTypes = EMPTY_ENDPOINT_PATTERN_MAP.get(kind)
  if (endpointTypes?.has(endpointType)) {
    return [{} as EmptyObject] // Returns [{}] for terminal nodes that need at least one element
  }
  return [] // Default: empty array (used for all other node types)
}

/**
 * Create a source endpoint for a fixed handle
 * @param nodeId - Node ID
 * @param kind - Legacy node kind
 * @param handle - Handle identifier (e.g., 'whenOpen', 'onTimeout')
 * @returns Source endpoint object
 */
function createFixedHandleSourceEndpoint(
  nodeId: string,
  kind: LegacyNodeKind,
  handle: string,
): {
  anchor: string
  is_source: boolean
  uuid: string
  target_uuid: null
  my_label: string
} {
  const numericId = extractNumericId(nodeId)
  const randomNumber = generateSecureRandomNumber()
  const handleUuid = `${kind}-${numericId}_${randomNumber}`
  const { anchor, label } = getHandleAnchorAndLabel(handle, kind)
  return {
    anchor,
    is_source: true,
    uuid: handleUuid,
    target_uuid: null,
    my_label: label,
  }
}

/**
 * Add fixed handles to sources array if they don't already exist
 * @param sources - Sources array to add handles to
 * @param nodeId - Node ID
 * @param kind - Legacy node kind
 * @param handles - Array of handle identifiers
 */
function addFixedHandlesIfMissing(
  sources: Array<{
    anchor?: string
    is_source?: boolean
    uuid?: string
    target_uuid?: string | null
    my_label?: string
    option_id?: string
  }>,
  nodeId: string,
  kind: LegacyNodeKind,
  handles: readonly string[],
  additionalExistingHandleKeys?: Set<string>,
): void {
  handles.forEach((handle) => {
    const { anchor, label } = getHandleAnchorAndLabel(handle, kind)
    const existingEndpoint = sources.find(
      (s) => s.my_label === label && s.anchor === anchor,
    )
    const handleKey = `${label}|${anchor}`
    const existsInAdditional = additionalExistingHandleKeys?.has(handleKey)
    if (!existingEndpoint && !existsInAdditional) {
      sources.push(createFixedHandleSourceEndpoint(nodeId, kind, handle))
    }
  })
}

function isRequiredFixedSourceHandle(
  kind: LegacyNodeKind,
  label: string,
  anchor: string,
): boolean {
  const handles = FIXED_SOURCE_HANDLES_BY_KIND.get(kind)
  if (!handles) {
    return false
  }

  return handles.some((handle) => {
    const mapped = getHandleAnchorAndLabel(handle, kind)
    return mapped.label === label && mapped.anchor === anchor
  })
}

/**
 * Map React Flow node type to legacy kind
 * @param type - React Flow node type
 * @returns Legacy node kind
 */
function mapNodeTypeToKind(type: SwitchboardNode['type']): LegacyNodeKind {
  if (type === 'start') {
    return 'start_node'
  }
  const kind = NODE_TYPE_TO_KIND_MAP[type]
  if (!kind) {
    throw new Error(
      `[Legacy Converter] Unknown React Flow node type "${String(type)}". Update NODE_TYPE_TO_KIND_MAP before saving.`,
    )
  }
  return kind
}

/**
 * Ensure fixed-handle routes have a single effective target before save.
 * Multiple different targets for the same fixed handle are ambiguous and can
 * silently reroute calls depending on endpoint ordering.
 */
function assertSingleEffectiveFixedHandleRoutes(
  flowData: SwitchboardFlowData,
): void {
  const conflicts = findFixedHandleRouteConflicts(flowData).flatMap(
    (conflict) => {
      const [firstRoute, ...additionalRoutes] = conflict.routes
      return additionalRoutes.map((route) =>
        firstRoute.targetNodeId === route.targetNodeId
          ? `${conflict.sourceKind}(${conflict.sourceNodeId}) ${conflict.sourceHandle}: duplicate route to "${route.targetNodeId}" (edge IDs: ${firstRoute.edgeId}, ${route.edgeId})`
          : `${conflict.sourceKind}(${conflict.sourceNodeId}) ${conflict.sourceHandle}: "${firstRoute.targetNodeId}" vs "${route.targetNodeId}" (edge IDs: ${firstRoute.edgeId}, ${route.edgeId})`,
      )
    },
  )

  if (conflicts.length > 0) {
    throw new Error(
      `[Legacy Converter] Invalid fixed-handle routing detected. Each fixed handle must resolve to one target. Conflicts: ${conflicts.join(
        '; ',
      )}`,
    )
  }
}

/**
 * Convert a React Flow node to legacy format
 * @param node - React Flow node to convert
 * @param edges - Array of React Flow edges
 * @param nodeToSourceEndpoints - Map of node IDs to source endpoint UUIDs
 * @param nodeToTargetEndpoints - Map of node IDs to target endpoint UUIDs
 * @returns Legacy format node
 */
/**
 * Strip Admin3-only fields from HTML that legacy doesn't use
 * These fields are ignored by legacy but add noise and may cause confusion
 *
 * @param html - HTML object to clean
 * @param kind - Legacy node kind
 * @returns Cleaned HTML object with Admin3-only fields removed
 */
function stripAdmin3OnlyFields(
  html: Record<string, unknown>,
  kind: LegacyNodeKind,
): Record<string, unknown> {
  const cleaned = { ...html }

  // Strip Admin3-only fields that are never used by legacy
  // These fields may come through as snake_case from previous conversions
  const admin3OnlyFields = [
    'allow_extension_as_option',
    'options', // options array should be removed after converting to children
    'send_from', // SMS: send_from is Admin3-only, should be converted to sms_number
    'validation_errors', // Admin3-only validation field, not part of legacy format
  ]

  admin3OnlyFields.forEach((field) => {
    delete cleaned[field]
  })

  // Node-specific cleanup
  if (kind === 'day_night') {
    // day_night: strip toggle_code and status (Admin3-only)
    delete cleaned['toggle_code']
    delete cleaned['status']
  }

  if (kind === 'change_caller_id') {
    // change_caller_id: mode should already be converted to new_caller_id_option
    // but strip it if it still exists
    delete cleaned['mode']
  }

  if (kind === 'interactive_webhook') {
    // interactive_webhook: strip allow_extension_as_option and options array
    // (iwebhook_allow_extension_dialing is the correct field)
    delete cleaned['allow_extension_as_option']
    delete cleaned['options']
  }

  if (kind === 'call_distribution') {
    // call_distribution: strip options array and option-* fields (Admin3-only)
    delete cleaned['options']
    // Remove all option-* fields (option-0, option-1, etc.) - database format does not have these
    Object.keys(cleaned).forEach((key) => {
      if (key.startsWith('option-')) {
        delete cleaned[key]
      }
    })
  }

  if (kind === 'auto_attendant') {
    // auto_attendant: strip Admin3-only metadata fields
    delete cleaned['extension_id']
    delete cleaned['options']
    // Note: line_id is NOT Admin3-only - it's a required legacy field that should always be null
    // Do NOT delete line_id - it must be present (as null) in legacy format
  }

  if (kind === 'transfer_rollover') {
    // transfer_rollover: strip extensions array and extension_id (Admin3-only)
    // Database format does not have extensions array, only transfer_rollover_id
    delete cleaned['extensions']
    delete cleaned['extension_id']
  }

  if (kind === 'sms') {
    // sms: strip send_from (Admin3-only field, should be converted to sms_number)
    // This is a safety net in case send_from wasn't already removed in convertSmsNotificationNodeData
    delete cleaned['send_from']
  }

  return cleaned
}

function convertNodeToLegacyFormat(
  node: SwitchboardNode,
  edges: SwitchboardEdge[],
  nodeToSourceEndpoints: Map<string, Map<string, string>>,
  nodeToTargetEndpoints: Map<string, Map<string, string>>,
  optionEndpointUuidMap: Map<string, string>,
  messagingGroups?: Array<{
    id: number
    phone_numbers?: Array<{
      number?: string
      number_for_display?: string
      is_primary_sms?: boolean
    }>
  }>,
): LegacyFormatNode {
  const kind = mapNodeTypeToKind(node.type)

  let html: Record<string, unknown> = {}
  if (node.data && node.type !== 'start') {
    html = convertNodeDataToHtml(
      node.data,
      node.type as NodeType,
      node.id,
      messagingGroups,
    )
  }

  const sources: Array<{
    anchor?: string
    is_source?: boolean
    uuid?: string
    target_uuid?: string | null
    my_label?: string
    option_id?: string
  }> = []
  const targets: Array<{
    anchor?: string
    is_source?: boolean
    uuid?: string
    source_uuid?: string | null
    my_label?: string
  }> = []

  // CRITICAL: Pre-compute target endpoint UUIDs - one per target node
  // Legacy format requires all source endpoints pointing to the same target node
  // to use the same target_uuid. We use the first edge's target endpoint UUID
  // for each target node.
  const targetNodeIdToEndpointUuid = new Map<string, string>()
  edges.forEach((edge) => {
    if (!targetNodeIdToEndpointUuid.has(edge.target)) {
      // Use the first edge's target endpoint UUID for this target node
      const firstTargetUuid = nodeToTargetEndpoints
        .get(edge.target)
        ?.get(edge.id)
      if (firstTargetUuid) {
        targetNodeIdToEndpointUuid.set(edge.target, firstTargetUuid)
      }
    }
  })

  // Track which nodes already have target endpoints created
  const nodesWithTargetEndpoints = new Set<string>()

  // Process edges to build endpoints
  // Note: optionEndpointUuidMap is passed as parameter and shared across all node conversions
  edges.forEach((edge) => {
    if (edge.source === node.id) {
      const sourceEndpointUuid = nodeToSourceEndpoints
        .get(node.id)
        ?.get(edge.id)

      if (!sourceEndpointUuid) {
        return
      }

      // CRITICAL: Use pre-computed target UUID so all source endpoints pointing to
      // the same target node use the same target_uuid (matching Legacy format)
      const targetEndpointUuid = targetNodeIdToEndpointUuid.get(edge.target)

      if (!targetEndpointUuid) {
        return
      }

      const { anchor, label } = getHandleAnchorAndLabel(
        edge.sourceHandle || undefined,
        kind,
      )

      const sourceEndpoint: {
        anchor: string
        is_source: boolean
        uuid: string
        target_uuid: string | null
        my_label: string
        option_id?: string
      } = {
        anchor,
        is_source: true,
        uuid: sourceEndpointUuid,
        target_uuid: targetEndpointUuid,
        my_label: label,
      }

      if (
        DYNAMIC_HANDLE_NODE_KINDS.has(kind) &&
        edge.sourceHandle &&
        edge.sourceHandle.startsWith('option-')
      ) {
        const optionIndex = parseInt(
          edge.sourceHandle.replace('option-', ''),
          10,
        )

        // CRITICAL: Get option ID from html.children[].id FIRST (source of truth)
        // This ensures the endpoint's option_id EXACTLY matches what Legacy will use
        // html.children[].id is set by convertOptionsToChildren, which either:
        // 1. Preserves option.id if it exists (and converts format if needed), OR
        // 2. Generates new ID using generateOptionId (which produces Legacy format)
        // Legacy uses html.children[].id to create DOM elements and jsPlumb endpoints,
        // so we MUST use the same ID for the endpoint's option_id field
        let optionId: string | undefined

        // Priority 1: Get from html.children[].id (what Legacy will actually use)
        if (
          optionIndex >= 0 &&
          html &&
          typeof html === 'object' &&
          'children' in html &&
          Array.isArray(html.children) &&
          html.children.length > optionIndex + 1 &&
          html.children[optionIndex + 1] &&
          typeof html.children[optionIndex + 1] === 'object' &&
          html.children[optionIndex + 1] !== null
        ) {
          const child = html.children[optionIndex + 1] as Record<
            string,
            unknown
          >
          // CRITICAL: Validate that this is NOT children[0] (which should be empty {})
          // children[0] should have no 'id' field - if it does, something is wrong
          if (optionIndex + 1 === 0) {
            // This should never happen due to optionIndex + 1, but add defensive check
            // If children[0] has an id, it means the structure is wrong
            const firstChild = html.children[0] as Record<string, unknown>
            if ('id' in firstChild && Object.keys(firstChild).length > 0) {
              // Don't create endpoint for children[0]
              // Note: This case should never occur if children array structure is correct
            }
          } else if (
            'id' in child &&
            typeof child.id === 'string' &&
            String(child.id).trim() !== ''
          ) {
            // Use the ID from children array - this is what Legacy will use
            let childId = String(child.id)
            // CRITICAL: Normalize option ID to remove node type prefix for auto_attendant and interactive_webhook
            // Legacy Admin's save condition splits by '_' and expects numeric node ID as first part
            // Legacy Admin (line 6979): strAutoAttendantSplit = strAutoAttendantOption.split('_')[0]
            // Legacy Admin (line 7095): strInteractiveWebhookSplit = strInteractiveWebhookOption.split('_')[0]
            // Condition: $elem.attr('id') == strAutoAttendantSplit (node ID == first part of option ID)
            // Format: "auto_attendant-1768860996222_option-1768861568573_0" -> "1768860996222_option-1768861568573_0"
            // Format: "interactive_webhook-1768860799809_option-1768861568573_0" -> "1768860799809_option-1768861568573_0"
            if (
              (kind === 'auto_attendant' || kind === 'interactive_webhook') &&
              childId.includes('-') &&
              childId.includes('_option-')
            ) {
              const match = childId.match(/-(\d+_option-.*)/)
              if (match && match[1]) {
                childId = match[1]
              }
            }
            optionId = childId
          }
        }

        // Priority 2: Fall back to options[].id only if children[].id not found
        // This handles edge cases where children might not be created yet
        if (!optionId) {
          if (
            node.data &&
            typeof node.data === 'object' &&
            'options' in node.data
          ) {
            const options = node.data.options as Array<
              Record<string, unknown> | undefined
            >
            if (
              Array.isArray(options) &&
              optionIndex >= 0 &&
              optionIndex < options.length &&
              options[optionIndex] &&
              typeof options[optionIndex] === 'object'
            ) {
              const option = options[optionIndex] as Record<string, unknown>
              // Accept any string ID - _option- format is preferred but not required
              // This ensures backward compatibility with different ID formats
              if (
                'id' in option &&
                typeof option.id === 'string' &&
                option.id.trim() !== ''
              ) {
                let fallbackOptionId = option.id
                // CRITICAL: Normalize option ID to remove node type prefix for auto_attendant and interactive_webhook
                // Legacy Admin's save condition splits by '_' and expects numeric node ID as first part
                if (
                  (kind === 'auto_attendant' ||
                    kind === 'interactive_webhook') &&
                  fallbackOptionId.includes('-') &&
                  fallbackOptionId.includes('_option-')
                ) {
                  const match = fallbackOptionId.match(/-(\d+_option-.*)/)
                  if (match && match[1]) {
                    fallbackOptionId = match[1]
                  }
                }
                optionId = fallbackOptionId
              }
            }
          }
        }

        if (optionId) {
          const optionEndpointUuid = `${optionId}_src`
          sourceEndpoint.option_id = optionId
          // CRITICAL: Option endpoints use special UUID format: {optionId}_src
          // Legacy code expects this format to identify option endpoints (see edit_switchboard.php:590, 3926)
          sourceEndpoint.uuid = optionEndpointUuid

          // CRITICAL: option_id MUST match children[].id exactly
          // Legacy Admin creates DOM element with id = children[].id
          // Legacy Admin attaches jsPlumb endpoint to that element using option_id
          // Legacy Admin creates connection with sourceId = option_id
          // Legacy Admin saves connection only if: sourceId == anchor.elementId
          // Therefore: option_id must equal children[].id for the condition to pass
          // We ensure this by getting optionId from html.children[index].id (line 431)

          // CRITICAL: Store mapping for target endpoint resolution using edge.id as key
          // Target endpoints use the same edge.id to retrieve this UUID (see line 489)
          // This ensures target.source_uuid matches source.uuid for option endpoints
          // edge.id is consistent across source and target endpoint creation because
          // both iterate over the same edges array in the same order
          optionEndpointUuidMap.set(edge.id, optionEndpointUuid)
        }
      }

      sources.push(sourceEndpoint)
    }

    if (edge.target === node.id) {
      // CRITICAL: Create target endpoint only once per node (matching Legacy format)
      // Legacy uses jsPlumb's maxConnections:-1 to allow multiple connections to the same target endpoint
      if (!nodesWithTargetEndpoints.has(node.id)) {
        // Use the pre-computed target UUID (same one used by all source endpoints)
        const targetEndpointUuid = targetNodeIdToEndpointUuid.get(node.id)

        if (!targetEndpointUuid) {
          return
        }

        // CRITICAL: Check if source is option endpoint - use correct UUID
        // Option endpoints use special UUID format: ${optionId}_src
        // The nodeToSourceEndpoints map has the standard UUID, but we need the option endpoint UUID
        // edge.id is the same key used when storing the option endpoint UUID (see line 468)
        // This ensures target.source_uuid matches source.uuid for option endpoints
        const optionEndpointUuid = optionEndpointUuidMap.get(edge.id)
        const sourceEndpointUuid =
          optionEndpointUuid ||
          nodeToSourceEndpoints.get(edge.source)?.get(edge.id)

        if (!sourceEndpointUuid) {
          return
        }

        targets.push({
          anchor: 'Left',
          is_source: false,
          uuid: targetEndpointUuid,
          source_uuid: sourceEndpointUuid, // Now uses correct option endpoint UUID when applicable
          my_label:
            typeof edge.label === 'string' ? edge.label : DEFAULT_LABELS.target,
        })
        nodesWithTargetEndpoints.add(node.id)
      }
      // Subsequent edges pointing to same node - skip (target endpoint already created)
    }
  })

  // CRITICAL: Create unconnected option endpoints for dynamic handle nodes
  // Legacy creates endpoints for ALL options in children array when loading from Legacy format
  // We must create endpoints for ALL options (connected or unconnected) to match Legacy behavior
  // This ensures round-trip compatibility and allows reconnection in Legacy Admin
  //
  // BACKEND CONSUMER REQUIREMENT (canvasjson.lua):
  // Backend processes connections by matching: option['option_id'] == item.id
  // - auto_attendant (line 1000): option['option_id'] == item.id
  // - interactive_webhook (line 600): option['option_id'] == item.id
  // - call_distribution (line 1028): option['option_id'] == item.id
  // Backend NEEDS endpoints in sources array to process connections correctly
  // If endpoints are missing, backend cannot route calls through option menus
  //
  // LEGACY ADMIN REQUIREMENT:
  // Legacy Admin's save logic requires: connectionSource.sourceId == connectionSource.endpoints[0].anchor.elementId
  // This means:
  // 1. DOM element must have id = option_id (from children[].id) ✅ We ensure this
  // 2. jsPlumb endpoint must be attached to that DOM element ✅ Legacy Admin does this
  // 3. jsPlumb connection sourceId must match DOM element id ✅ Legacy Admin does this
  // 4. When saving, sourceId must equal anchor.elementId ✅ This is Legacy Admin's condition
  //
  // CRITICAL: option_id in sources MUST match children[].id exactly
  // This ensures:
  // - Backend can match option_id with children[].id to process connections
  // - Legacy Admin can create DOM element with id = children[].id
  // - Legacy Admin can attach endpoint to that element using option_id
  // - Legacy Admin can create connection with sourceId = option_id
  // - Legacy Admin can save connection when sourceId == anchor.elementId
  //
  // NOTE: Even if Legacy Admin fails to save endpoints (save condition fails),
  // endpoints are still in JSON when Admin3 saves, so backend functionality works.
  // When Admin3 loads again, it reconstructs missing endpoints from children[].
  if (
    DYNAMIC_HANDLE_NODE_KINDS.has(kind) &&
    html &&
    typeof html === 'object' &&
    'children' in html &&
    Array.isArray(html.children)
  ) {
    // Collect option IDs from connected endpoints
    const connectedOptionIds = new Set<string>()
    sources.forEach((source) => {
      if (source.option_id) {
        connectedOptionIds.add(source.option_id)
      }
    })

    // CRITICAL: Build map of option endpoint UUIDs to target_uuid from unconnectedSourceEndpoints
    // This allows us to preserve target_uuid when creating endpoints from html.children
    // We match by UUID (not option_id) because option_ids might be regenerated between conversions
    const unconnectedSourceEndpointsMap = new Map<string, string | null>()
    if (
      node.data &&
      typeof node.data === 'object' &&
      'unconnectedSourceEndpoints' in node.data
    ) {
      const unconnectedSourceEndpoints = node.data.unconnectedSourceEndpoints
      if (Array.isArray(unconnectedSourceEndpoints)) {
        unconnectedSourceEndpoints.forEach((endpoint) => {
          if (endpoint && typeof endpoint === 'object') {
            const ep = endpoint as Record<string, unknown>
            if (ep.uuid && typeof ep.uuid === 'string') {
              const targetUuid =
                ep.target_uuid && typeof ep.target_uuid === 'string'
                  ? ep.target_uuid
                  : null
              unconnectedSourceEndpointsMap.set(ep.uuid, targetUuid)
            }
          }
        })
      }
    }

    // CRITICAL: Always create endpoints for all options, regardless of connection status
    // Remove condition that only created endpoints when SOME were connected
    // This fixes the edge case where ALL options are unconnected
    // Legacy expects all options to have endpoints (even if target_uuid: null)
    html.children.forEach((child, index) => {
      // Skip children[0] (empty object placeholder)
      if (
        index > 0 &&
        child &&
        typeof child === 'object' &&
        'id' in child &&
        typeof child.id === 'string' &&
        String(child.id).includes('_option-')
      ) {
        let optionId = String(child.id)
        // CRITICAL: Normalize option ID to remove node type prefix for auto_attendant and interactive_webhook
        // Legacy Admin's save condition splits by '_' and expects numeric node ID as first part
        // Format: "auto_attendant-1768860996222_option-1768861568573_0" -> "1768860996222_option-1768861568573_0"
        // Format: "interactive_webhook-1768860799809_option-1768861568573_0" -> "1768860799809_option-1768861568573_0"
        if (
          (kind === 'auto_attendant' || kind === 'interactive_webhook') &&
          optionId.includes('-') &&
          optionId.includes('_option-')
        ) {
          const match = optionId.match(/-(\d+_option-.*)/)
          if (match && match[1]) {
            optionId = match[1]
          }
        }
        // Only create endpoint if it doesn't already exist (from connected edge)
        if (!connectedOptionIds.has(optionId)) {
          // CRITICAL: Check if this endpoint has target_uuid in unconnectedSourceEndpoints
          // Match by UUID (format: ${optionId}_src) to preserve target_uuid from previous conversion
          // If so, preserve it (for connected endpoints reconstructed from target references)
          const optionUuid = `${optionId}_src`
          const targetUuid = unconnectedSourceEndpointsMap.get(optionUuid)

          // CRITICAL: option_id MUST match children[].id exactly
          // Legacy Admin creates DOM element with id = children[].id
          // Legacy Admin attaches jsPlumb endpoint to that element
          // Legacy Admin creates connection with sourceId = option_id
          // Legacy Admin saves connection only if: sourceId == anchor.elementId
          // Therefore: option_id must equal children[].id for the condition to pass
          // Create option endpoint (connected or unconnected based on unconnectedSourceEndpoints)
          sources.push({
            anchor: 'Right',
            is_source: true,
            uuid: optionUuid, // Format: ${optionId}_src
            target_uuid: targetUuid !== undefined ? targetUuid : null,
            option_id: optionId, // CRITICAL: Must match children[].id exactly
            my_label: 'Drag',
          })
        }
      }
    })
  }

  // Create source endpoints for fixed handles that don't have connections
  const fixedHandles = FIXED_SOURCE_HANDLES_BY_KIND.get(kind)

  if (fixedHandles) {
    const preservedFixedHandleKeys = new Set<string>()
    if (
      node.data &&
      typeof node.data === 'object' &&
      'unconnectedSourceEndpoints' in node.data &&
      Array.isArray(node.data.unconnectedSourceEndpoints)
    ) {
      const unconnectedSourceEndpoints = node.data.unconnectedSourceEndpoints
      unconnectedSourceEndpoints.forEach((endpoint) => {
        if (!endpoint || typeof endpoint !== 'object') {
          return
        }
        const ep = endpoint as Record<string, unknown>
        const label = typeof ep.my_label === 'string' ? ep.my_label : undefined
        const anchor = typeof ep.anchor === 'string' ? ep.anchor : undefined
        if (!label || !anchor) {
          return
        }

        const isRequiredFixedHandle = fixedHandles.some((handle) => {
          const mapped = getHandleAnchorAndLabel(handle, kind)
          return mapped.label === label && mapped.anchor === anchor
        })

        if (isRequiredFixedHandle) {
          preservedFixedHandleKeys.add(`${label}|${anchor}`)
        }
      })
    }

    // CRITICAL: For interactive_webhook, fixed endpoints (On Invalid, On Timeout) must ALWAYS exist
    // Even if unconnected, they must be present in the saved data
    // addFixedHandlesIfMissing ensures they exist even when not connected
    addFixedHandlesIfMissing(
      sources,
      node.id,
      kind,
      fixedHandles,
      preservedFixedHandleKeys,
    )
    // Defensive check: Verify fixed endpoints exist for interactive_webhook
    if (kind === 'interactive_webhook') {
      const hasOnInvalid = sources.some(
        (s) => s.anchor === 'TopLeft' && s.my_label === 'On Invalid',
      )
      const hasOnTimeout = sources.some(
        (s) => s.anchor === 'TopRight' && s.my_label === 'On Timeout',
      )
      if (!hasOnInvalid || !hasOnTimeout) {
        // This should never happen if addFixedHandlesIfMissing works correctly
        // But add defensive check to ensure they exist
        // Re-add them if missing (shouldn't be necessary, but safety net)
        if (!hasOnInvalid) {
          sources.push(
            createFixedHandleSourceEndpoint(node.id, kind, 'onInvalid'),
          )
        }
        if (!hasOnTimeout) {
          sources.push(
            createFixedHandleSourceEndpoint(node.id, kind, 'onTimeout'),
          )
        }
      }
    }
  }

  // CRITICAL: Recreate unconnected regular node endpoints from node data
  // These were preserved when loading from legacy (in edgeConverters.ts)
  // and must be recreated when converting back to legacy format
  if (
    node.data &&
    typeof node.data === 'object' &&
    'unconnectedSourceEndpoints' in node.data
  ) {
    const unconnectedSourceEndpoints = node.data.unconnectedSourceEndpoints
    if (Array.isArray(unconnectedSourceEndpoints)) {
      unconnectedSourceEndpoints.forEach((endpoint) => {
        if (endpoint && typeof endpoint === 'object') {
          const ep = endpoint as Record<string, unknown>

          // Preserve legacy oddities by default: only de-duplicate by UUID.
          // Exception: required fixed handles must be unique by semantic key
          // (label + anchor), regardless of connection state. This avoids
          // converter-introduced duplicate fixed handles across save cycles.
          // Example: a stale unconnected "Closed" endpoint merged alongside a
          // newly connected "Closed" edge should not produce two "Closed@Right"
          // endpoints.
          const existingByUuid = sources.find((s) => s.uuid === ep.uuid)
          const endpointLabel =
            typeof ep.my_label === 'string' ? ep.my_label : undefined
          const endpointAnchor =
            typeof ep.anchor === 'string' ? ep.anchor : undefined
          const incomingTargetUuid =
            ep.target_uuid && typeof ep.target_uuid === 'string'
              ? String(ep.target_uuid)
              : null
          const isDuplicateFixedHandleBySemantic =
            !!endpointLabel &&
            !!endpointAnchor &&
            isRequiredFixedSourceHandle(kind, endpointLabel, endpointAnchor) &&
            sources.some(
              (source) =>
                source.my_label === endpointLabel &&
                source.anchor === endpointAnchor,
            )

          if (!existingByUuid && !isDuplicateFixedHandleBySemantic && ep.uuid) {
            sources.push({
              anchor: endpointAnchor || 'Right',
              is_source: true,
              uuid: String(ep.uuid),
              // CRITICAL: Preserve target_uuid if it exists (for connected endpoints reconstructed from target references)
              // If target_uuid is not null/undefined, this endpoint is connected and should be saved as such
              target_uuid: incomingTargetUuid,
              my_label: endpointLabel || 'Drag',
              option_id: ep.option_id ? String(ep.option_id) : undefined,
            })
          }
        }
      })
    }
  }

  // CRITICAL: Recreate unconnected target endpoints from node data
  // These were preserved when loading from legacy (in edgeConverters.ts)
  // and must be recreated when converting back to legacy format
  if (
    node.data &&
    typeof node.data === 'object' &&
    'unconnectedTargetEndpoints' in node.data
  ) {
    const unconnectedTargetEndpoints = node.data.unconnectedTargetEndpoints
    if (Array.isArray(unconnectedTargetEndpoints)) {
      unconnectedTargetEndpoints.forEach((endpoint) => {
        if (endpoint && typeof endpoint === 'object') {
          const ep = endpoint as Record<string, unknown>
          // Check if endpoint already exists (avoid duplicates)
          const existing = targets.find((t) => t.uuid === ep.uuid)
          if (!existing && ep.uuid) {
            targets.push({
              anchor: (ep.anchor as string) || 'Left',
              is_source: false,
              uuid: String(ep.uuid),
              source_uuid: null, // Unconnected target endpoint - must be null, not omitted
              my_label: (ep.my_label as string) || 'Drag',
            })
          }
        }
      })
    }
  }

  // CRITICAL: Unconnected endpoint preservation requirements
  //
  // Fixed Handles (schedule, day_night, queue, transfer_rollover, auto_attendant, interactive_webhook, ai_agent_v1):
  // - MUST always exist in saved JSON, even when unconnected
  // - MUST have target_uuid: null (not omitted) when unconnected
  // - This is handled by addFixedHandlesIfMissing() which ensures all fixed handles exist
  // - createFixedHandleSourceEndpoint() creates them with target_uuid: null
  //
  // Dynamic Handles (option endpoints for auto_attendant, call_distribution, interactive_webhook):
  // - Unconnected option endpoints are created above (lines 534-558) with target_uuid: null
  // - These are preserved to allow reconnection in Legacy Admin
  //
  // Regular Handles (simple_dial, external_number, etc.):
  // - Legacy typically only saves connected endpoints for regular handles
  // - However, if legacy data ALREADY HAS unconnected endpoints, we preserve them for round-trip compatibility
  // - Unconnected regular endpoints are recreated from node.data.unconnectedSourceEndpoints (above)
  //
  // Target Endpoints:
  // - Unconnected target endpoints are recreated from node.data.unconnectedTargetEndpoints (above)
  //
  // IMPORTANT: Do NOT filter out endpoints with target_uuid: null or source_uuid: null - they must be preserved

  // P1-3 Fix: For queue, always keep fixed handles (onTimeout, onEscape)
  // Queue is like schedule and day_night - it must always have fixed handles present
  // The spec compliance validator requires queue to always have both handles
  // Note: Fixed handles are added by addFixedHandlesIfMissing and should remain even if unconnected
  // Queue targets should be preserved if they exist in source data to maintain round-trip compatibility
  // (Filter removed to preserve unconnected targets if they exist in source data)

  const nodeIdForLegacy =
    kind === 'start_node' ? 'start_node' : extractNumericId(node.id)

  let heightValue: string
  if (node.height && typeof node.height === 'number') {
    heightValue = `${node.height}px`
  } else if (
    node.height &&
    typeof node.height === 'string' &&
    node.height !== 'auto'
  ) {
    heightValue = node.height
  } else {
    if (kind === 'start_node') {
      heightValue = '50px'
    } else {
      heightValue = '121.81922px'
    }
  }

  // Build the base node structure
  const baseNode = {
    top: `${node.position.y}px`,
    left: `${node.position.x}px`,
    width: node.width ? `${node.width}px` : '220px',
    height: heightValue,
    id: nodeIdForLegacy,
    kind,
    endpoints: {
      // Use correct empty endpoint pattern based on node type and endpoint type
      // Terminal nodes (direct_line, leave_voicemail, hang_up, etc.) use [{}] for empty sources
      // All other nodes use [] for empty endpoints
      sources:
        sources.length > 0
          ? sources
          : (getEmptyEndpointPattern(kind, 'sources') as typeof sources),
      targets:
        targets.length > 0
          ? targets
          : (getEmptyEndpointPattern(kind, 'targets') as typeof targets),
    },
  }

  // start_node should NOT have html property (not even undefined)
  if (kind === 'start_node') {
    return baseNode as LegacyFormatNode
  }

  // All other nodes must have html property
  return {
    ...baseNode,
    html:
      Object.keys(html).length > 0
        ? stripAdmin3OnlyFields(html, kind) // Final cleanup before returning
        : {},
  } as LegacyFormatNode
}

/**
 * Convert React Flow format to legacy format for API compatibility
 *
 * @param flowData - React Flow format data with nodes and edges
 * @param messagingGroups - Optional messaging groups for converting SMS sendFrom IDs to phone numbers
 * @returns Legacy format data structure compatible with the API
 *
 * @example
 * ```ts
 * const flowData = {
 *   nodes: [{ id: 'start', type: 'start', ... }],
 *   edges: []
 * }
 * const legacyData = convertReactFlowToLegacyFormat(flowData, messagingGroups)
 * ```
 */
export function convertReactFlowToLegacyFormat(
  flowData: SwitchboardFlowData,
  messagingGroups?: Array<{
    id: number
    phone_numbers?: Array<{
      number?: string
      number_for_display?: string
      is_primary_sms?: boolean
    }>
  }>,
): LegacyFormatData {
  const legacyFormatNodes: LegacyFormatData['nodes'] = {}

  // Filter nodes before conversion to exclude invalid nodes.
  // We only validate/convert the same effective node set that will be saved.
  const nodesToConvert = flowData.nodes.filter((node) => {
    // Basic validation: node must have type and id
    if (!node.type || !node.id) {
      return false
    }

    // Check if node has deletion flag in data (defensive check for future)
    if (node.data && typeof node.data === 'object') {
      if ('deleted' in node.data && node.data.deleted === true) {
        return false
      }
      if (
        'shouldExcludeFromSave' in node.data &&
        node.data.shouldExcludeFromSave === true
      ) {
        return false
      }
    }

    return true
  })

  const nodeIdToKind = new Map<string, LegacyNodeKind>()
  nodesToConvert.forEach((node) => {
    const kind = mapNodeTypeToKind(node.type)
    nodeIdToKind.set(node.id, kind)
  })

  // Drop dangling edges whose source/target no longer exists in the effective
  // save set (e.g. deleted/filtered nodes) so endpoint mapping cannot throw.
  const edgesToConvert = flowData.edges.filter(
    (edge) => nodeIdToKind.has(edge.source) && nodeIdToKind.has(edge.target),
  )

  // Save-time assertion: fixed-handle routes must be deterministic.
  assertSingleEffectiveFixedHandleRoutes(flowData)

  const usedRandomNumbers = new Set<number>()
  const getUniqueRandom = () => getUniqueRandomNumber(usedRandomNumbers)

  const { nodeToSourceEndpoints, nodeToTargetEndpoints } = buildEndpointMaps(
    edgesToConvert,
    nodeIdToKind,
    getUniqueRandom,
  )

  // CRITICAL: Map to store option endpoint UUIDs for target endpoint resolution
  // When source endpoint is an option endpoint, it uses UUID format: ${optionId}_src
  // This map stores edge.id -> optionEndpointUuid and is shared across all node conversions
  // so that target endpoints can reference the correct option endpoint UUIDs
  const optionEndpointUuidMap = new Map<string, string>()

  nodesToConvert.forEach((node) => {
    const kind = mapNodeTypeToKind(node.type)
    const legacyNode = convertNodeToLegacyFormat(
      node,
      edgesToConvert,
      nodeToSourceEndpoints,
      nodeToTargetEndpoints,
      optionEndpointUuidMap,
      messagingGroups,
    )

    // CRITICAL: start_node must be saved as object (not array)
    // All other node types must be saved as arrays (even if only one node)
    // This matches legacy format requirements (see SWITCHBOARD_BE_CONSUMER_DOC.md Section 1.2)
    if (kind === 'start_node') {
      // Validate start_node is not already set (should only be one)
      if (legacyFormatNodes.start_node) {
        console.warn(
          `[Legacy Converter] Multiple start_node nodes detected. Only one start_node is allowed. Overwriting previous start_node.`,
        )
      }
      legacyFormatNodes.start_node = legacyNode
    } else {
      // All other node types are arrays
      if (!legacyFormatNodes[kind]) {
        legacyFormatNodes[kind] = []
      }
      const kindArray = legacyFormatNodes[kind] as LegacyFormatNode[]
      if (Array.isArray(kindArray)) {
        kindArray.push(legacyNode)
      } else {
        // Defensive check: if kindArray is not an array, something is wrong
        console.error(
          `[Legacy Converter] Node type ${kind} is not an array. This violates legacy format requirements.`,
        )
      }
    }
  })

  const allLegacyNodes: LegacyFormatNode[] = []
  Object.values(legacyFormatNodes).forEach((nodeOrArray) => {
    if (Array.isArray(nodeOrArray)) {
      allLegacyNodes.push(...nodeOrArray)
    } else if (nodeOrArray) {
      allLegacyNodes.push(nodeOrArray)
    }
  })

  // CRITICAL: Normalize target.source_uuid after all nodes are converted.
  // Source/target endpoint creation can happen in node iteration order, so target endpoints
  // may be created before dynamic option endpoint UUIDs are finalized (${optionId}_src).
  // Re-link by target UUID to ensure target.source_uuid always references an actual source endpoint UUID.
  const targetUuidToSourceUuid = new Map<string, string>()
  allLegacyNodes.forEach((node) => {
    const sources = Array.isArray(node.endpoints?.sources)
      ? node.endpoints.sources
      : []
    sources.forEach((source) => {
      if (
        source &&
        typeof source === 'object' &&
        source.uuid &&
        source.target_uuid &&
        typeof source.uuid === 'string' &&
        typeof source.target_uuid === 'string'
      ) {
        // Preserve first mapping for deterministic output.
        if (!targetUuidToSourceUuid.has(source.target_uuid)) {
          targetUuidToSourceUuid.set(source.target_uuid, source.uuid)
        }
      }
    })
  })

  allLegacyNodes.forEach((node) => {
    const targets = Array.isArray(node.endpoints?.targets)
      ? node.endpoints.targets
      : []
    targets.forEach((target) => {
      if (
        target &&
        typeof target === 'object' &&
        target.uuid &&
        typeof target.uuid === 'string'
      ) {
        const linkedSourceUuid = targetUuidToSourceUuid.get(target.uuid)
        if (linkedSourceUuid) {
          target.source_uuid = linkedSourceUuid
        }
      }
    })
  })

  // CRITICAL: Validate node ID uniqueness
  // Legacy requires unique node IDs within a canvas - duplicates would cause lookup failures
  const nodesById = new Map<string, LegacyFormatNode>()
  const duplicateNodeIds: string[] = []
  allLegacyNodes.forEach((node) => {
    if (nodesById.has(node.id)) {
      // Found duplicate node ID - log for debugging
      duplicateNodeIds.push(node.id)
      console.warn(
        `[Legacy Converter] Duplicate node ID detected: ${node.id} (kind: ${node.kind}). This may cause backend routing issues.`,
      )
    }
    nodesById.set(node.id, node)
  })

  // If duplicates found, log warning but continue conversion
  // This allows partial/incomplete data to be converted, but warns about potential issues
  if (duplicateNodeIds.length > 0) {
    console.warn(
      `[Legacy Converter] Found ${duplicateNodeIds.length} duplicate node ID(s): ${duplicateNodeIds.join(', ')}. Backend may fail to route calls correctly.`,
    )
  }

  allLegacyNodes.forEach((node) => {
    const validationResult = validateEndpointReferences(
      node as unknown as LegacyNode,
    )

    if (!validationResult.valid) {
      // Validation failed but we continue conversion
      // This allows partial/incomplete data to be converted
    }

    // CRITICAL: Ensure sources is always an array before calling array methods
    // Legacy format may have sources as {} (empty object) for direct_line nodes,
    // or it may be undefined/null. Always ensure it's an array before iteration.
    const sources = Array.isArray(node.endpoints?.sources)
      ? node.endpoints.sources
      : []
    sources.forEach((source) => {
      if (source.target_uuid) {
        const parts = source.target_uuid.split('-')
        if (parts.length >= 2) {
          const targetNodeId = parts[1].split('_')[0]
          const targetNode = nodesById.get(targetNodeId)
          if (targetNode?.endpoints) {
            validateTargetEndpointExists(
              source.target_uuid,
              targetNode.endpoints as LegacyNode['endpoints'],
            )
          }
        }
      }
    })
  })

  // CRITICAL: Validate bidirectional connection matching
  // Source endpoint's target_uuid must match target endpoint's uuid
  // Target endpoint's source_uuid must match source endpoint's uuid
  // This ensures connections are properly linked in both directions
  allLegacyNodes.forEach((node) => {
    const sources = Array.isArray(node.endpoints?.sources)
      ? node.endpoints.sources
      : []
    sources.forEach((source) => {
      if (source.target_uuid) {
        // Extract target node ID from target_uuid
        const parts = source.target_uuid.split('-')
        if (parts.length >= 2) {
          const targetNodeId = parts[1].split('_')[0]
          const targetNode = nodesById.get(targetNodeId)
          if (targetNode?.endpoints?.targets) {
            // Find target endpoint with matching uuid
            const targetEndpoint = targetNode.endpoints.targets.find(
              (t) => t.uuid === source.target_uuid,
            )
            if (targetEndpoint) {
              // Verify bidirectional matching: source target_uuid matches target uuid
              if (targetEndpoint.uuid !== source.target_uuid) {
                console.warn(
                  `[Legacy Converter] Connection mismatch: Source target_uuid (${source.target_uuid}) does not match target uuid (${targetEndpoint.uuid})`,
                )
              }
              // Verify target source_uuid matches source uuid
              if (targetEndpoint.source_uuid !== source.uuid) {
                console.warn(
                  `[Legacy Converter] Connection mismatch: Target source_uuid (${targetEndpoint.source_uuid}) does not match source uuid (${source.uuid})`,
                )
              }
            } else {
              // Target endpoint not found - this is a known legacy bug (missing target endpoints)
              // Log as warning, not error, since converter handles this gracefully
              console.warn(
                `[Legacy Converter] Target endpoint not found for target_uuid: ${source.target_uuid} (target node: ${targetNodeId}). This is a known legacy data issue.`,
              )
            }
          }
        }
      }
    })
  })

  // Ensure all node types are present (matching legacy behavior)
  ALL_LEGACY_NODE_TYPES.forEach((nodeType) => {
    if (!(nodeType in legacyFormatNodes)) {
      if (nodeType !== 'start_node') {
        legacyFormatNodes[nodeType] = []
      }
    }
  })

  return { nodes: legacyFormatNodes }
}
